import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, EmptyState } from '@ds/feedback';
import { SearchField } from '@ds/forms';
import { Menu, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { normalizarPlano } from '@/data/planosDeSaude';
import type { PlanoDeSaude } from '@/data/planosDeSaude';
import { dados } from '@/data/porta';
import { rotaPorId } from '@/router/rotas';
import { useRotaAtual } from '@/router/useRotaAtual';
import { pode } from '@/session/sessao';
import { DetalheDoPlanoModal } from './planos/DetalheDoPlanoModal';
import { PlanoModal } from './planos/PlanoModal';

export interface CargaDosPlanos {
    planos: PlanoDeSaude[];
}

export async function carregarPlanosDeSaude(): Promise<CargaDosPlanos> {
    return { planos: await dados().planosDeSaude() };
}

function semAcento(texto: string): string {
    return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Planos de saúde — porte de `ui_kits/vecchia_app/PlanosScreen.jsx`.
 *
 * O dashboard do protótipo multiplicava números fixos por um "fator" do
 * período. Aqui só entra o que existe: quantos pacientes têm cada plano na
 * ficha e o repasse cadastrado. A comparação de valor por procedimento pede
 * uma tabela de preço por plano, que ainda não existe — e a tela diz isso.
 */
export function PlanosDeSaude() {
    const { planos } = useLoaderData() as CargaDosPlanos;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const handle = useRotaAtual();
    const podeEditar = pode('configuracoes');
    const [emEdicao, setEmEdicao] = useState<PlanoDeSaude | 'novo' | null>(null);

    const secao = handle?.subrota?.path === 'cadastrados' ? 'cadastrados' : 'dashboard';
    const busca = params.get('q') ?? '';
    const idAberto = params.get('plano');

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            return proximos;
        }, { replace: chave !== 'plano' });
    };

    const ativos = planos.filter((p) => p.ativo);
    const porPacientes = [...planos].sort((a, b) => b.pacientes - a.pacientes || a.nome.localeCompare(b.nome, 'pt-BR'));
    const lider = porPacientes[0] && porPacientes[0].pacientes > 0 ? porPacientes[0] : undefined;
    const totalDePacientes = planos.reduce((s, p) => s + p.pacientes, 0);
    const maiorContagem = Math.max(1, ...planos.map((p) => p.pacientes));
    const conveniosComRepasse = ativos.filter((p) => p.tipo === 'Convênio' && p.repassePercentual !== undefined);
    const repasseMedio = conveniosComRepasse.length > 0
        ? conveniosComRepasse.reduce((s, p) => s + (p.repassePercentual ?? 0), 0) / conveniosComRepasse.length
        : undefined;
    const filtrados = planos.filter((p) => !busca.trim() || semAcento(`${p.nome} ${p.operadora ?? ''} ${p.tipo}`).includes(semAcento(busca.trim())));

    const alternarAtivo = async (p: PlanoDeSaude) => {
        try {
            await dados().salvarPlanoDeSaude(p.id, { ...normalizarPlano(p), ativo: !p.ativo });
            aviso({
                title: p.ativo ? 'Plano desativado' : 'Plano ativado',
                description: p.ativo ? `${p.nome} · sai das opções da ficha do paciente` : p.nome,
                tone: p.ativo ? 'warning' : 'success',
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar o plano', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<PlanoDeSaude>[] = [
        {
            key: 'nome', header: 'Plano', sortable: true,
            render: (p) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                    <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: p.cor, flex: '0 0 auto' }} />
                    <span>
                        <strong style={{ display: 'block', fontWeight: 600 }}>{p.nome}</strong>
                        {(p.operadora || p.registroAns) && (
                            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                {[p.operadora, p.registroAns ? `ANS ${p.registroAns}` : null].filter(Boolean).join(' · ')}
                            </span>
                        )}
                    </span>
                </span>
            ),
        },
        { key: 'tipo', header: 'Tipo', render: (p) => <Badge tone={p.tipo === 'Convênio' ? 'info' : p.tipo === 'Particular' ? 'neutral' : 'warning'}>{p.tipo}</Badge> },
        { key: 'tabelaDePrecos', header: 'Tabela', render: (p) => p.tabelaDePrecos ?? '—' },
        { key: 'repassePercentual', header: 'Repasse', align: 'right', render: (p) => (p.repassePercentual === undefined ? '—' : `${p.repassePercentual.toLocaleString('pt-BR')}%`) },
        { key: 'prazoRecebimento', header: 'Prazo', render: (p) => p.prazoRecebimento ?? '—' },
        { key: 'pacientes', header: 'Pacientes', align: 'right', sortable: true },
        { key: 'ativo', header: 'Situação', render: (p) => <Badge tone={p.ativo ? 'success' : 'neutral'} dot>{p.ativo ? 'Ativo' : 'Inativo'}</Badge> },
    ];
    if (podeEditar) {
        colunas.push({
            key: 'acoes', header: '', align: 'right', width: 56,
            render: (p) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={210}
                        onSelect={(id) => {
                            if (id === 'editar') setEmEdicao(p);
                            else if (id === 'ativo') void alternarAtivo(p);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Ações de ${p.nome}`} />}
                        items={[
                            { id: 'editar', label: 'Editar plano', icon: 'pencil' },
                            { id: 'ativo', label: p.ativo ? 'Desativar plano' : 'Ativar plano', icon: p.ativo ? 'circle-pause' : 'circle-play' },
                        ]}
                    />
                </span>
            ),
        });
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Planos de saúde"
                description="Convênios, cartões de desconto e particular — repasse, prazo e quantos pacientes usam cada um"
                actions={(
                    <>
                        <ExportMenu<PlanoDeSaude>
                            label="Exportar" size="md"
                            payload={{
                                title: 'Planos de saúde', subtitle: `${planos.length} planos`, rows: planos,
                                columns: [
                                    { header: 'Plano', value: (p) => p.nome },
                                    { header: 'Tipo', value: (p) => p.tipo },
                                    { header: 'Operadora', value: (p) => p.operadora ?? '' },
                                    { header: 'Registro ANS', value: (p) => p.registroAns ?? '' },
                                    { header: 'Repasse', value: (p) => (p.repassePercentual === undefined ? '' : `${p.repassePercentual}%`) },
                                    { header: 'Prazo', value: (p) => p.prazoRecebimento ?? '' },
                                    { header: 'Pacientes', value: (p) => String(p.pacientes) },
                                    { header: 'Situação', value: (p) => (p.ativo ? 'Ativo' : 'Inativo') },
                                ],
                            }}
                        />
                        {podeEditar && <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setEmEdicao('novo')}>Novo plano</Button>}
                    </>
                )}
            >
                <Tabs
                    ariaLabel="Seções dos planos de saúde" activeId={secao}
                    onChange={(id) => navigate({ pathname: `/app/planos/${id}`, search: params.toString() ? `?${params}` : '' })}
                    tabs={(rotaPorId('planos-de-saude')?.children ?? []).map((c) => ({
                        id: c.path, label: c.label, count: c.path === 'cadastrados' ? planos.length : undefined,
                    }))}
                />
            </PageHeader>

            {secao === 'dashboard' ? (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
                        <KpiCard label="Planos ativos" value={String(ativos.length)} hint={`de ${planos.length} cadastrados`} icon={<Icon name="shield-plus" size={16} />} />
                        <KpiCard label="Pacientes com plano na ficha" value={String(totalDePacientes)} hint="inclui particular" icon={<Icon name="users" size={16} />} />
                        <KpiCard
                            label="Plano com mais pacientes" value={lider?.nome ?? '—'}
                            hint={lider ? `${lider.pacientes} ${lider.pacientes === 1 ? 'paciente' : 'pacientes'}` : 'nenhum paciente com plano'}
                            icon={<Icon name="award" size={16} />}
                        />
                        <KpiCard
                            label="Repasse médio dos convênios" value={repasseMedio === undefined ? '—' : `${Math.round(repasseMedio)}%`}
                            hint="média simples do cadastro" icon={<Icon name="percent" size={16} />}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, alignItems: 'start' }}>
                        <Card title="Pacientes por plano" subtitle="Pelo plano gravado na ficha de cada paciente" padding={18}>
                            {planos.length === 0 ? (
                                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum plano cadastrado.</p>
                            ) : (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {porPacientes.map((p) => (
                                        <li key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 3, background: p.cor, flex: '0 0 auto', opacity: p.ativo ? 1 : 0.4 }} />
                                            <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-ui)', fontSize: 13, color: p.ativo ? 'var(--text-primary)' : 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {p.nome}{p.ativo ? '' : ' (inativo)'}
                                            </span>
                                            <span aria-hidden="true" style={{ width: 96, height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--neutral-100)', overflow: 'hidden', flex: '0 0 auto' }}>
                                                <span style={{ display: 'block', width: `${Math.round((p.pacientes / maiorContagem) * 100)}%`, height: '100%', background: p.cor, opacity: p.ativo ? 1 : 0.4 }} />
                                            </span>
                                            <span style={{ width: 34, textAlign: 'right', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{p.pacientes}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>

                        <Card title="Procedimentos por plano" subtitle="Valor recebido por procedimento, comparado ao custo direto" padding={0}>
                            <EmptyState
                                icon="table" title="A tabela de preço por plano ainda não existe"
                                description="Comparar o valor de cada plano com o custo do procedimento pede o preço de cada procedimento em cada plano — e esse cadastro ainda não está no sistema."
                            />
                        </Card>
                    </div>
                </>
            ) : (
                <>
                    {!podeEditar && (
                        <Alert tone="neutral" title="Só consulta" dismissible={false}>
                            Só quem administra a clínica cadastra, edita e desativa planos de saúde.
                        </Alert>
                    )}
                    <FilterBar
                        search={<SearchField value={busca} onChange={(v) => trocarParam('q', v || null)} placeholder="Buscar plano ou operadora" width={260} />}
                        resultLabel={`${filtrados.length} de ${planos.length}`}
                    />
                    <DataTable
                        columns={colunas} rows={filtrados} rowKey="id"
                        onRowClick={(p) => trocarParam('plano', p.id)}
                        rowLabel={(p) => p.nome}
                        emptyMessage={busca ? `Nenhum plano com “${busca}”.` : 'Nenhum plano cadastrado.'}
                    />
                </>
            )}

            {idAberto && !emEdicao && (
                <DetalheDoPlanoModal
                    plano={planos.find((p) => p.id === idAberto) ?? null} podeEditar={podeEditar}
                    onFechar={() => trocarParam('plano', null)}
                    onEditar={setEmEdicao}
                />
            )}
            {emEdicao && (
                <PlanoModal
                    key={emEdicao === 'novo' ? 'novo' : emEdicao.id} alvo={emEdicao}
                    onFechar={() => setEmEdicao(null)} onSalvo={() => revalidator.revalidate()}
                />
            )}
        </div>
    );
}
